// ============================================================
// Email ingest — BROWSER I/O adapter (pdf.js text + render, tesseract.js OCR)
// Thin wrapper around the validated pure logic in parseThread.ts.
// Runtime decision A (+B fallback): see memory email-ingest-runtime-decision.
//   A: pdf.js text extraction + tesseract.js OCR of the rasterized iOS header band
//   B: GPT-4o vision fallback (injected callback) when OCR confidence is low
// ============================================================

import * as pdfjsLib from "pdfjs-dist";
// Vite worker URL pattern:
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import Tesseract from "tesseract.js";
import { parseThread, ParseInput } from "./parseThread";
import { ExportRoute, ParsedEmailThread } from "./types";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const OCR_CONFIDENCE_FLOOR = 70; // below this, try the vision fallback (B)
const HEADER_BAND_FRACTION = 0.22; // top 22% of page 1 holds the iOS header card

export interface ExtractOptions {
  /** Vision fallback (option B). Given a PNG blob of the header band, return OCR text.
   *  App wires this to an Edge proxy that calls GPT-4o vision on the cleared OpenAI tier. */
  visionOcr?: (headerPng: Blob) => Promise<string>;
  /** Progress callback for the upload UI. */
  onProgress?: (phase: string, pct?: number) => void;
}

function routeFromProducer(producer: string, creator: string): ExportRoute {
  const p = `${producer} ${creator}`.toLowerCase();
  if (p.includes("skia") || p.includes("chrome") || p.includes("mozilla")) return "gmail_chrome_print";
  if (p.includes("ios") || p.includes("quartz") || p.includes("mail")) return "ios_mail_print";
  return "generic";
}

/** Reconstruct text lines from a pdf.js text-content stream using the hasEOL flag. */
async function extractTextLines(pdf: pdfjsLib.PDFDocumentProxy): Promise<string[]> {
  const lines: string[] = [];
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();
    let cur = "";
    for (const item of content.items as Array<{ str: string; hasEOL?: boolean }>) {
      cur += item.str;
      if (item.hasEOL) { lines.push(cur); cur = ""; }
    }
    if (cur.trim()) lines.push(cur);
  }
  return lines;
}

/** Render the top band of page 1 to a PNG blob (the iOS rasterized header lives here). */
async function renderHeaderBand(pdf: pdfjsLib.PDFDocumentProxy): Promise<Blob | null> {
  const page = await pdf.getPage(1);
  const scale = 300 / 72; // ~300 DPI for OCR
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height * HEADER_BAND_FRACTION);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  // render full page then keep the clipped band (pdf.js renders to the canvas origin)
  await page.render({ canvasContext: ctx, viewport }).promise;
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

async function ocrBandLines(band: Blob, opts: ExtractOptions): Promise<string[]> {
  const { data } = await Tesseract.recognize(band, "eng");
  const conf = data.confidence ?? 0;
  let text = data.text || "";
  // Option B fallback: low OCR confidence and a vision callback is available
  if (conf < OCR_CONFIDENCE_FLOOR && opts.visionOcr) {
    opts.onProgress?.("vision-fallback");
    try { text = await opts.visionOcr(band); } catch { /* keep tesseract text */ }
  }
  return text.split("\n").map((l: string) => l.trimEnd()).filter((l: string) => l.trim());
}

/** Main entry: File (PDF) -> validated structured thread. */
export async function parseEmailPdf(file: File, opts: ExtractOptions = {}): Promise<ParsedEmailThread> {
  opts.onProgress?.("loading");
  const buf = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;

  const meta = await pdf.getMetadata().catch(() => null);
  const info = (meta?.info ?? {}) as { Producer?: string; Creator?: string; Title?: string };
  const route = routeFromProducer(info.Producer || "", info.Creator || "");

  opts.onProgress?.("extracting-text");
  const text_lines = await extractTextLines(pdf);

  let ocr_header_lines: string[] | undefined;
  if (route === "ios_mail_print") {
    opts.onProgress?.("ocr-header");
    const band = await renderHeaderBand(pdf);
    if (band) ocr_header_lines = await ocrBandLines(band, opts);
  }

  const input: ParseInput = {
    source_file: file.name,
    doc_title: info.Title || null,
    export_route: route,
    text_lines,
    ocr_header_lines,
  };
  opts.onProgress?.("parsing");
  const result = parseThread(input);
  opts.onProgress?.("done", 100);
  return result;
}
